import { Outlet } from 'react-router-dom';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/outline';
import { RiArrowRightLine } from '@remixicon/react';

import Button from 'components/Button';
import MaxWidthPage from 'components/MaxWidthPage';
import Page from 'components/Page/Page';

import { useCopyToClipboard } from 'hooks/useCopyToClipboard';

import { trackMoreApisClaimClicked, trackMoreApisViewDocsClicked, trackMoreApisCopyCodeClicked } from 'utils/Tracking';

import serpwowFavicon from 'assets/icons/traject/serpwow-favicon.png';
import rainforestFavicon from 'assets/icons/traject/rainforest-favicon.png';
import bluecartFavicon from 'assets/icons/traject/bluecart-favicon.png';
import countdownFavicon from 'assets/icons/traject/countdown-favicon.png';
import redcircleFavicon from 'assets/icons/traject/redcircle-favicon.png';

const DISCOUNT_CODE = 'SAPI15';

const API_DATA = [
  {
    name: 'SerpWow',
    platform: 'Google',
    category: 'SERP',
    domain: 'serpwow.com',
    description: 'SERP data from Google, Bing, and more. Supports AI Overviews, shopping results, local pack, image search, and news.',
    endpoints: ['search', 'shopping', 'news', 'images', 'local'],
    signupUrl: 'https://app.serpwow.com/signup?utm_source=scraperapi&utm_medium=cross_sell&utm_campaign=q2-crosssell-traject&utm_content=serpwow-card',
    docsUrl: 'https://docs.trajectdata.com/serpwow',
    favicon: serpwowFavicon,
    accentColor: '#1890ff',
  },
  {
    name: 'Rainforest API',
    platform: 'Amazon',
    category: 'Ecommerce',
    domain: 'rainforestapi.com',
    description: 'Comprehensive Amazon product, search, review, and seller data. Covers every Amazon page type across all domains.',
    endpoints: ['product', 'search', 'reviews', 'bestsellers', 'seller'],
    signupUrl: 'https://app.rainforestapi.com/signup?utm_source=scraperapi&utm_medium=cross_sell&utm_campaign=q2-crosssell-traject&utm_content=rainforest-card',
    docsUrl: 'https://docs.trajectdata.com/rainforestapi',
    favicon: rainforestFavicon,
    accentColor: '#243746',
  },
  {
    name: 'BlueCart API',
    platform: 'Walmart',
    category: 'Ecommerce',
    domain: 'bluecartapi.com',
    description: 'Reliable Walmart product and search data. Returns structured listings, prices, availability, and reviews in real time.',
    endpoints: ['product', 'search', 'reviews', 'category'],
    signupUrl: 'https://app.bluecartapi.com/signup?utm_source=scraperapi&utm_medium=cross_sell&utm_campaign=q2-crosssell-traject&utm_content=bluecart-card',
    docsUrl: 'https://docs.trajectdata.com/bluecartapi',
    favicon: bluecartFavicon,
    accentColor: '#1890ff',
  },
  {
    name: 'Countdown API',
    platform: 'eBay',
    category: 'Ecommerce',
    domain: 'countdownapi.com',
    description: 'Real-time data from any eBay domain worldwide. Products, reviews, search results — clean, structured JSON output.',
    endpoints: ['search', 'product', 'reviews', 'autocomplete'],
    signupUrl: 'https://app.countdownapi.com/signup?utm_source=scraperapi&utm_medium=cross_sell&utm_campaign=q2-crosssell-traject&utm_content=countdown-card',
    docsUrl: 'https://docs.trajectdata.com/countdownapi',
    favicon: countdownFavicon,
    accentColor: '#242f3d',
  },
  {
    name: 'RedCircle API',
    platform: 'Target',
    category: 'Ecommerce',
    domain: 'redcircleapi.com',
    description: 'Fast, accurate Target product and search results. Listings, pricing, availability, store inventory, and reviews.',
    endpoints: ['product', 'search', 'reviews', 'store_inventory'],
    signupUrl: 'https://app.redcircleapi.com/signup?utm_source=scraperapi&utm_medium=cross_sell&utm_campaign=q2-crosssell-traject&utm_content=redcircle-card',
    docsUrl: 'https://docs.trajectdata.com/redcircleapi',
    favicon: redcircleFavicon,
    accentColor: '#bb261a',
  },
];

const FAQ_DATA = [
  {
    question: 'Do I need to sign up for a new account to use these endpoints?',
    answer: 'Yes — each endpoint runs on its own platform, so you\'ll need to create a separate account for each one you want to use. Signing up takes about 60 seconds, and your 15% discount is applied automatically with code SAPI15.',
  },
  {
    question: 'What\'s the difference between these endpoints and my current ScraperAPI plan?',
    answer: 'ScraperAPI gives you access to any website, raw or structured. These endpoints go deeper on specific sources — returning dozens of data fields per request with no post-processing needed. Think of it as a specialist layer on top of your scraping stack.',
  },
  {
    question: 'Where can I get help with these endpoints?',
    answer: 'For questions about the discount, integration, or which endpoint to start with, ScraperAPI support can help. For in-depth technical questions, documentation and support are available via each product\'s dashboard.',
  },
  {
    question: 'Will my ScraperAPI pricing change?',
    answer: 'No. Your existing ScraperAPI plan stays the same. Each Traject Data endpoint has its own pricing, and you only pay for what you use.',
  },
];

function PromoBlock() {
  const [copied, copyToClipboard] = useCopyToClipboard();

  return (
    <div className="border-2 border-yellow-400 rounded-lg p-6 bg-yellow-50 dark:bg-yellow-950/10">
      <p className="text-sm font-medium text-yellow-700 dark:text-yellow-500 mb-2">
        Sign up for a free account to get started
      </p>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
        15% off your first month, just for ScraperAPI users
      </h2>
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex flex-col border border-gray-300 dark:border-gray-600 rounded-md overflow-hidden bg-white dark:bg-gray-800 min-w-[200px]">
          <span className="text-xs text-gray-500 dark:text-gray-400 px-4 pt-2 pb-0.5 uppercase tracking-wide">
            Discount code
          </span>
          <span className="text-2xl font-bold text-gray-900 dark:text-white px-4 pb-3 tracking-widest">
            {DISCOUNT_CODE}
          </span>
        </div>
        <button
          type="button"
          onClick={() => { copyToClipboard(DISCOUNT_CODE); trackMoreApisCopyCodeClicked(); }}
          className="flex items-center gap-2 px-5 py-3 bg-gray-900 dark:bg-gray-700 text-white text-sm font-semibold rounded-md hover:bg-gray-800 dark:hover:bg-gray-600 transition-colors tracking-wider"
        >
          {copied ? 'COPIED!' : 'COPY CODE'}
        </button>
      </div>
    </div>
  );
}

function ApiCard({ api }: Readonly<{ api: typeof API_DATA[number] }>) {
  return (
    <div className="flex flex-col border border-gray-200 dark:border-gray-700 rounded-lg p-5 bg-white dark:bg-gray-800 gap-4 border-l-4" style={{ borderLeftColor: api.accentColor }}>
      <div className="flex items-start justify-between gap-2">
        <img src={api.favicon} alt={`${api.name} favicon`} className="w-8 h-8 rounded" />
        <div className="flex gap-1.5 flex-wrap justify-end">
          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded-full">
            {api.platform}
          </span>
          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 rounded-full">
            {api.category}
          </span>
        </div>
      </div>

      <div>
        <h3 className="font-bold text-gray-900 dark:text-white text-base">{api.name}</h3>
        <span className="text-xs text-blue-600 dark:text-blue-400">
          {api.domain}
        </span>
      </div>

      <p className="text-sm text-gray-600 dark:text-gray-400 flex-1">{api.description}</p>

      <div>
        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
          Key Endpoints
        </p>
        <div className="flex flex-wrap gap-1.5">
          {api.endpoints.map((ep) => (
            <span
              key={ep}
              className="inline-flex items-center px-2 py-0.5 text-xs bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 rounded"
            >
              {ep}
            </span>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3 mt-auto pt-2">
        <Button
          text="CLAIM 15% OFF"
          href={api.signupUrl}
          icon={{ Icon: RiArrowRightLine, size: 'w-4 h-4', position: 'trailing' }}
          className="button button-primary"
          size="SM"
          onClick={() => trackMoreApisClaimClicked(api.name)}
        />
        <a
          href={api.docsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white font-medium"
          onClick={() => trackMoreApisViewDocsClicked(api.name)}
        >
          VIEW DOCS
        </a>
      </div>
    </div>
  );
}

function FaqSection() {
  return (
    <div className="flex flex-col gap-y-2">
      {FAQ_DATA.map((item) => (
        <Disclosure key={item.question}>
          {({ open }) => (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <DisclosureButton className="flex w-full items-center justify-between px-5 py-4 text-left bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <span className="font-medium text-gray-900 dark:text-white text-sm pr-4">
                  {item.question}
                </span>
                {open
                  ? <ChevronUpIcon className="w-5 h-5 text-gray-500 shrink-0" />
                  : <ChevronDownIcon className="w-5 h-5 text-gray-500 shrink-0" />
                }
              </DisclosureButton>
              <DisclosurePanel className="px-5 py-4 text-sm text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700">
                {item.answer}
              </DisclosurePanel>
            </div>
          )}
        </Disclosure>
      ))}
    </div>
  );
}

export default function MoreApisPage() {
  return (
    <MaxWidthPage fullWidth>
      <Page browserTitle="More APIs">
        <div className="flex flex-col gap-y-10 pb-10">

          {/* Section 1 -- Header */}
          <div className="flex flex-col gap-y-3">
            <span className="inline-flex w-fit items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 uppercase tracking-wide">
              Announcement
            </span>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              More data. Deeper insights. Powered by Traject Data
            </h1>
            <p className="text-base text-gray-600 dark:text-gray-400 max-w-2xl">
              We're expanding ScraperAPI with Traject Data's endpoints to bring you ready-made, structured endpoints for the sources that matter most. More data fields, less parsing work.
            </p>
          </div>

          {/* Section 2 -- Promo block */}
          <PromoBlock />

          {/* Section 3 -- API gallery */}
          <div className="flex flex-col gap-y-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Pick your data source — get structured results, instantly
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                All powered by Traject Data
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {API_DATA.map((api) => (
                <ApiCard key={api.name} api={api} />
              ))}
            </div>
          </div>

          {/* Section 4 -- FAQ */}
          <div className="flex flex-col gap-y-4">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Quick answers
            </h2>
            <FaqSection />
          </div>

        </div>
      </Page>

      <Outlet />
    </MaxWidthPage>
  );
}
